<template>
  <b-card
    :class="[
      'member-product-card',
      { 'member-product-card--out-of-stock': isOutOfStock },
      { 'member-product-card--in-cart': cartQuantity > 0 }
    ]"
    no-body
  >
    <div class="product-image-wrapper">
      <img
        v-if="product.public_img_src || product.product_img_src"
        :src="product.public_img_src || product.product_img_src"
        :alt="product.name"
        class="product-image"
      />
      <div v-else class="product-image-placeholder">
        <i class="fas fa-cannabis"></i>
      </div>
      <b-badge
        v-if="isOutOfStock"
        variant="danger"
        class="out-of-stock-overlay-badge"
      >Out of Stock</b-badge>
    </div>
    <b-card-body>
      <h3 class="product-name">{{ product.name }}</h3>
      <span class="product-price">{{ formattedPrice }}</span>
      <stock-badge
        :quantity="product.stock_quantity || 0"
        :low-threshold="product.low_stock_threshold || 5"
      />
      <div v-if="cartQuantity > 0" class="in-cart-indicator">
        <b-badge variant="primary" pill>In cart ({{ cartQuantity }})</b-badge>
      </div>
      <a
        href="#"
        class="view-details-link"
        @click.prevent="$emit('view-details', product)"
      >View Details</a>
      <div class="product-actions">
        <div v-if="cartQuantity > 0" class="qty-controls">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="qty-btn"
            aria-label="Decrease quantity"
            @click="decrementQty"
            @keyup.enter="decrementQty"
            @keyup.space.prevent="decrementQty"
          >-</b-button>
          <span class="qty-display">{{ cartQuantity }}</span>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="qty-btn"
            aria-label="Increase quantity"
            @click="incrementQty"
            @keyup.enter="incrementQty"
            @keyup.space.prevent="incrementQty"
          >+</b-button>
        </div>
        <b-button
          v-else
          variant="primary"
          :disabled="isOutOfStock || isAdding"
          class="add-to-cart-btn"
          @click="handleAddToCart"
          @keyup.enter="handleAddToCart"
          @keyup.space.prevent="handleAddToCart"
        >
          <b-spinner v-if="isAdding" small />
          <span v-else>Add to Cart</span>
        </b-button>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import StockBadge from "@/components/shared/stockBadge";

export default {
  name: "memberProductCard",
  components: { StockBadge },
  props: {
    product: {
      type: Object,
      required: true,
    },
    cartQuantity: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isAdding: false,
    };
  },
  computed: {
    isOutOfStock() {
      return (this.product.stock_quantity || 0) === 0;
    },
    formattedPrice() {
      const price = parseFloat(this.product.price) || 0;
      return `R${price.toFixed(2)}`;
    },
  },
  methods: {
    handleAddToCart() {
      if (this.isOutOfStock || this.isAdding) return;
      this.isAdding = true;
      this.$emit("add-to-cart", {
        productId: this.product.id,
        quantity: 1,
      });
      setTimeout(() => {
        this.isAdding = false;
      }, 300);
    },
    incrementQty() {
      this.$emit("update-cart-quantity", {
        productId: this.product.id,
        newQty: this.cartQuantity + 1,
      });
    },
    decrementQty() {
      this.$emit("update-cart-quantity", {
        productId: this.product.id,
        newQty: this.cartQuantity - 1,
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "~@/design/tokens/brand";
@import "~@/design/tokens/surfaces";
@import "~@/design/tokens/typography";
@import "~@/design/tokens/spacing";

.member-product-card {
  background: $surface-card;
  border: 1px solid $border-default;
  border-radius: 8px;
  overflow: hidden;
  transition: opacity 0.15s ease, border-color 0.15s ease;

  &--out-of-stock {
    opacity: 0.4;
  }

  &--in-cart {
    border-color: $brand-primary;
  }
}

.product-image-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  background: $surface-elevated;
  overflow: hidden;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $text-muted;
  font-size: 48px;
}

.out-of-stock-overlay-badge {
  position: absolute;
  top: $space-2;
  right: $space-2;
}

.product-name {
  font-size: $type-h3;
  font-weight: $font-weight-semibold;
  color: $text-primary;
  margin-bottom: $space-1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-price {
  font-size: $type-body-lg;
  font-weight: $font-weight-bold;
  color: $brand-secondary;
  display: block;
  margin-bottom: $space-2;
}

.in-cart-indicator {
  margin-top: $space-2;
  margin-bottom: $space-2;
}

.view-details-link {
  font-size: 0.85rem;
  color: $brand-primary;
  display: block;
  margin-top: $space-2;
}

.product-actions {
  margin-top: $space-3;
}

.qty-controls {
  display: flex;
  align-items: center;
  gap: $space-3;
}

.qty-btn {
  min-width: 44px;
  min-height: 44px;
  font-size: $type-body-lg;
  font-weight: $font-weight-bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qty-display {
  font-size: $type-body-lg;
  font-weight: $font-weight-semibold;
  color: $text-primary;
  min-width: 32px;
  text-align: center;
}

.add-to-cart-btn {
  width: 100%;
  min-height: 44px;
  font-weight: $font-weight-semibold;
}
</style>
